/**
 * Objectives (03 §3): what each mission asks for, evaluated from data. This module owns
 * `world.objective` and everything in `ObjectiveState`.
 *
 *   kinds.ts         how success is measured, one evaluator per `ObjectiveKind`
 *   kindsMulti.ts    the expansion's counting kinds: useObjects, plantAtTargets, visitZones, holdZone
 *   target.ts        killTarget / lootTarget: the man the mission is about, and his flight
 *   questItem.ts     deliverItem / reachDistance: the thing that rides in a pod
 *   rules.ts         quiet rules, alert consequences, not-seen-near, the signal
 *   alertEffects.ts  the expansion's alert consequences
 *   timed.ts         timedEvent rules on the ground clock; timedEffects.ts what each one does
 *   blackout.ts      switches that put searchlights and automated guns out
 *   charges.ts       bombs that wait: for midnight, or for the column to come over them
 *   useAction.ts     the hold-still Interact at a useObjects target
 *   captives.ts      escorts: following, being hit, boarding first
 *   officer.ts       mission 4: surrender, flight, bolting
 *   disguise.ts      the stolen uniform (02 §6.4)
 *   dormant.ts       spawns that wait for a trigger
 *   executioners.ts  mission 10: the men sent to kill the hostages
 *   interactions.ts  what the Interact button does for the mission
 *   noise.ts         which of this tick's bangs were the ranger's
 *
 * A failed objective does not end the mission: the ranger may still extract, and it scores
 * as a failure (02 §10).
 */

import type { Ctx } from '../ctx';
import type { Captive, ConsequenceState, MissionDef, ObjectiveState } from '../types';
import { stepBlackouts } from './blackout';
import { stepCaptives } from './captives';
import { stepDisguise } from './disguise';
import { stepDormant } from './dormant';
import { stepExecutionerRoster } from './executioners';
import { holdBombs } from './charges';
import { initialTotal, stepCells, stepKind } from './kinds';
import { readPlayerNoise, rememberOrdnance } from './noise';
import { stepOfficer } from './officer';
import { stepQuestItem } from './questItem';
import { stepRules } from './rules';
import { stepTarget } from './target';
import { createTimedStates, stepSearchParties, stepTimedEvents } from './timed';
import { tidyUseAction } from './useAction';

export { escortsAtLadder } from './captives';
export { isPrisoner } from './common';
export { disguiseHides } from './disguise';
export { type ObjectiveInteract, objectiveInteractOption, performObjectiveInteract } from './interactions';
export { useActionTicks } from './kindsMulti';

/** A defector is not there until his `contactArrives`; with no such event he waits from the start. Everyone else is held. */
function initialCaptiveState(def: MissionDef, kind: Captive['kind']): Captive['state'] {
  if (kind !== 'defector') return 'held';
  const comes = def.objective.rules.some((r) => r.type === 'timedEvent' && r.effect === 'contactArrives');
  return comes ? 'absent' : 'waiting';
}

export function createObjectiveState(def: MissionDef): ObjectiveState {
  const consequences: ConsequenceState[] = [];
  def.objective.rules.forEach((rule, index) => {
    if (rule.type === 'alertConsequence') consequences.push({ rule: index, ticks: -1, spent: false });
  });
  return {
    tagsRecovered: false,
    complete: false,
    failed: false,
    failReason: null,
    done: 0,
    total: initialTotal(def),
    dwellTicks: 0,
    enteredZone: false,
    contactBroken: false,
    consequenceTicks: -1,
    signalled: false,
    quietTicks: 0,
    captives: def.captives.map((c) => ({
      id: c.id, kind: c.kind, x: c.x, z: c.z, prevX: c.x, prevZ: c.z, facing: 0,
      state: initialCaptiveState(def, c.kind), travelled: 0, moving: false, wounded: false,
      climbTicks: 0, awayTicks: 0, path: [], pathIndex: 0, stuckTicks: 0,
    })),
    pickupX: def.pickup.x,
    pickupZ: def.pickup.z,
    disguised: false,
    scrutinyTicks: 0,
    groundTicks: 0,
    used: false,
    opened: false,
    wentLoud: false,
    consequences,
    officerId: -1,
    officerFleeing: false,
    surrenderTicks: 0,
    woken: { nearObjective: false, objectiveOpened: false },
    ordnance: [],
    executioners: [],
    usedPropIds: [],
    usingPropId: -1,
    plantedPropIds: [],
    zoneTicks: (def.objective.zones ?? []).map(() => 0),
    zonesDone: (def.objective.zones ?? []).map(() => false),
    holdTicks: 0,
    targetEnemyId: -1,
    targetFleeing: false,
    targetGone: false,
    targetLooted: false,
    // Resolved on the first ground tick: in its pod, or on his back when the mission has no such pod.
    item: def.objective.questItem ? 'pod' : 'none',
    armedTick: -1,
    completeTick: -1,
    timed: createTimedStates(def),
    windowOpen: false,
    blackouts: (def.blackoutGroups ?? []).map(() => false),
    shootOrdered: false,
    chargesFired: 0,
    zoneEnteredTick: -1,
    spanDeed: false,
    targetAboardId: -1,
  };
}

/** Is Recall allowed right now? (Mission 11 forbids it.) */
export function recallAllowed(def: MissionDef): boolean {
  return !def.objective.rules.some((r) => r.type === 'recall' && r.mode === 'disabled');
}

/** Runs on the ground after projectiles, so `ctx.events` already holds this tick's shots, blasts and sightings. */
export function stepObjectives(ctx: Ctx): void {
  const w = ctx.world;
  const o = w.objective;
  const before = o.done;
  const noise = readPlayerNoise(ctx);

  // The world goes on whether or not the objective can still be had...
  stepDisguise(ctx, noise);
  stepCaptives(ctx);
  stepOfficer(ctx);
  stepTarget(ctx);
  stepCells(ctx);
  stepDormant(ctx);
  stepTimedEvents(ctx);
  stepSearchParties(ctx);
  stepExecutionerRoster(ctx);
  holdBombs(ctx);
  stepQuestItem(ctx);
  stepBlackouts(ctx);
  tidyUseAction(ctx);

  // ...but a lost objective is not judged again.
  if (!o.failed) stepRules(ctx, noise);
  if (!o.failed) stepKind(ctx);
  if (o.done !== before) ctx.events.push({ type: 'objective', destroyed: o.done, total: o.total });
  // Timed events counted `from: 'complete'` start here (the Osprey that comes when the crossing has been held).
  if (o.complete && o.completeTick < 0) o.completeTick = o.groundTicks;

  rememberOrdnance(w);
  o.groundTicks++;
  // The HUD reads these two; they mirror the objective's progress.
  w.targetsDestroyed = o.done;
  w.targetsTotal = o.total;
}
